import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PropTypes } from "prop-types";
import qs from "qs";
import React from "react";
import autoBind from "react-autobind";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import JobActions from "~/actions/job_actions";

import LeadDrawerActions from "~/actions/lead_drawer_actions";
import Drawer from "~/components/drawers/drawer";
import ResumeDrawer from "~/components/drawers/lead_drawer/ResumeDrawer";
import Dialer from "~/components/modals/dialer";
import AdditionalDetailsSection from "~/features/customFields/AdditionalDetailsSection";
import { checkFeatureFlag, getFeatureFlag } from "~/helpers/FeatureFlagChecker";
import { renderComponent } from "~/helpers/reactRoot";
import LeadDrawerStore from "~/stores/lead_drawer_store";
import LeadDrawerAgentAlerts from "./lead_drawer/details/LeadDrawerAgentAlerts/LeadDrawerAgentAlerts";
import LeadDrawerCourtedDetails from "./lead_drawer/details/LeadDrawerCourtedDetails";
import LeadDrawerSocialLinks from "./lead_drawer/details/LeadDrawerSocialLinks";
import LeadDrawerDocuments from "./lead_drawer/details/lead_drawer_documents";
import LeadDrawerJobs from "./lead_drawer/details/lead_drawer_jobs";
import LeadDrawerMainDetails from "./lead_drawer/details/lead_drawer_main_details";
import LeadDrawerPipelineDetails from "./lead_drawer/details/lead_drawer_pipeline_details";
import LeadDrawerRecruitingDetails from "./lead_drawer/details/lead_drawer_recruiting_details";
import LeadDrawerRelitixDetails from "./lead_drawer/details/lead_drawer_relitix_details";
import LeadDrawerRetentionDetails from "./lead_drawer/details/lead_drawer_retention_details";
import LeadDrawerTasks from "./lead_drawer/details/lead_drawer_tasks";
import LeadDrawerCampaignSubscriptions from "./lead_drawer/lead_drawer_campaign_subscriptions";
import LeadDrawerForms, {
  isFormNameValid,
} from "./lead_drawer/lead_drawer_forms";
import LeadDrawerHeader from "./lead_drawer/lead_drawer_header";
import LeadDrawerLabels from "./lead_drawer/lead_drawer_labels";
import LeadDrawerNotes from "./lead_drawer/lead_drawer_notes";
import LeadDrawerTimeline from "./lead_drawer/lead_drawer_timeline";

/**
 * Class LeadDrawer - and going to be exported as a state Container
 * @extends React.Component
 */

class LeadDrawer extends React.Component {
  static getStores() {
    return [LeadDrawerStore];
  }

  /**
   * Determine when to re-render LeadDrawer container
   * @param prevState
   * @returns {Object} Store current state
   */
  static calculateState(prevState) {
    return {
      leadDrawerState: LeadDrawerStore.getState(),
    };
  }

  isResumeTabEnabled = checkFeatureFlag(
    process.env.SHAKAPACKER_PUBLIC_RESUME_TAB_FLAG || true,
  );

  isDmsEnabled = checkFeatureFlag(
    process.env.SHAKAPACKER_PUBLIC_ENABLE_DOCUMENT_MANAGEMENT_FLAG,
  );

  constructor(props) {
    super(props);

    this.state = {
      leadDrawerState: LeadDrawerStore.getState(),
      dialer: {
        id: null,
        body: "",
        callSid: "",
        callStatus: "",
      },
      activeKey: "timeline",
      isDialerOpen: false,
      customFieldsBusy: false,
      activityRefreshVersion: 0,
      customFieldsRefreshVersion: 0,
    };

    this.switchForm = this.switchForm.bind(this);
    this.loadLead = this.loadLead.bind(this);
    this.recordIndex = this.recordIndex.bind(this);
    this.handlePrevClick = this.handlePrevClick.bind(this);
    this.handleNextClick = this.handleNextClick.bind(this);
    this.onLeadDrawerStoreChange = this.onLeadDrawerStoreChange.bind(this);
    this.handleOpenDialer = this.handleOpenDialer.bind(this);
    this.setDialerField = this.setDialerField.bind(this);

    autoBind(this);
  }

  componentDidMount() {
    const { defaultAction } = this.props;
    this.listener = LeadDrawerStore.addListener(this.onLeadDrawerStoreChange);
    this.loadLead(this.props);

    if (defaultAction) {
      // someone wanted to open to this action, honor the request and force it
      window.location.hash = `#${defaultAction}`;
    }

    if (window.location.hash) {
      // hash set from url, wasn't being passed from somewhere
      this.switchForm(window.location.hash.replace("#", ""));
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.loadLead(nextProps);
  }

  componentWillUnmount() {
    LeadDrawerActions.unloadLead();

    if (this.listener) {
      this.listener.remove();
    }
  }

  handlePrevClick() {
    const { leadDrawerState } = this.state;
    const { query, rowIdx, table } = this.props;
    const { recordIndex } = leadDrawerState;

    let index = recordIndex;
    if (typeof index === "undefined") {
      index = this.recordIndex(query, rowIdx);
    }

    let prevRecordIndex = index - 1;
    if (prevRecordIndex < 0) {
      prevRecordIndex = 0;
    }

    // Use appropriate navigation method based on table scope
    if (table && table.scope === "candidates") {
      JobActions.loadCandidateByIndex(
        query,
        prevRecordIndex,
        table.jobId,
        (leadId, candidateRecordIndex) => {
          LeadDrawerActions.loadLead(leadId, {
            recordIndex: candidateRecordIndex,
          });
        },
      );
    } else {
      LeadDrawerActions.loadLeadByIndex(query, prevRecordIndex);
    }
  }

  handleNextClick() {
    const { leadDrawerState } = this.state;
    const { query, totalCount, rowIdx, table } = this.props;
    const { recordIndex } = leadDrawerState;

    let index = recordIndex;
    if (typeof index === "undefined") {
      index = this.recordIndex(query, rowIdx);
    }

    let nextRecordIndex = index + 1;
    if (nextRecordIndex > totalCount - 1) {
      nextRecordIndex = totalCount - 1;
    }

    // Use appropriate navigation method based on table scope
    if (table && table.scope === "candidates") {
      JobActions.loadCandidateByIndex(
        query,
        nextRecordIndex,
        table.jobId,
        (leadId, candidateRecordIndex) => {
          LeadDrawerActions.loadLead(leadId, {
            recordIndex: candidateRecordIndex,
          });
        },
      );
    } else {
      LeadDrawerActions.loadLeadByIndex(query, nextRecordIndex);
    }
  }

  handleOpenDialer(phoneNumber, phoneSlot = null) {
    const { currentForm, leadDrawerState } = this.state;
    const { lead } = leadDrawerState;

    this.setState({ isDialerOpen: true });

    renderComponent(
      <Dialer
        containerID="dialer-modal"
        modalClass="modal no-pointer-events"
        dialogClass="modal-dialog modal-sm"
        lead={lead}
        leadPhone={phoneNumber}
        phoneSlot={phoneSlot}
        currentForm={currentForm}
        switchForm={this.switchForm}
        setDialerField={this.setDialerField}
        onDialerClose={this.enableDrawerControls}
      />,
      document.getElementById("dialer-modal"),
    );
  }

  onLeadDrawerStoreChange() {
    const { lead, leadDrawerStoreAction } = LeadDrawerStore.getState();

    this.setState({ leadDrawerState: LeadDrawerStore.getState() });

    if (!lead) {
      return;
    }

    // Check lead type and redirect if needed.
    // Skip when opened from advanced filter results — the filter view spans
    // all pipelines, so redirecting would destroy the filter state.
    if (!this.props.skipPipelineRedirect) {
      const currentPath = window.location.pathname;
      if (lead.recruiting && currentPath.includes("/retention")) {
        const newPath = currentPath.replace("/retention", "/recruiter");
        setTimeout(() => {
          browserHistory.push(
            newPath + window.location.search + window.location.hash,
          );
        }, 0);
        return;
      }

      if (!lead.recruiting && currentPath.includes("/recruiter")) {
        if (currentPath.includes("/recruiter/jobs")) {
          // don't redirect to retention if on jobs page
          return;
        }
        // Don't push to /retention when the user is gated out of it —
        // NavBar would push them straight back to /recruiter, producing
        // a redirect ping-pong that manifests as an infinite "Loading
        // Lead..." spinner.
        if (
          checkFeatureFlag(
            process.env.SHAKAPACKER_PUBLIC_RESTRICT_RETENTION_ACCESS_FLAG,
          )
        ) {
          return;
        }
        const newPath = currentPath.replace("/recruiter", "/retention");
        setTimeout(() => {
          browserHistory.push(
            newPath + window.location.search + window.location.hash,
          );
        }, 0);
        return;
      }
    }

    const params = GlobalContainer.urlParams();
    params.lead = lead.id;

    const searchQuery = qs.stringify(
      Object.assign(params, { _t: Rails.helpers.currentTeam.id }),
      { arrayFormat: "brackets" },
    );

    // push browser state to opening lead URL
    browserHistory.push(
      `${window.location.pathname}?${decodeURI(searchQuery)}${
        window.location.hash
      }`,
      { lead: lead.id },
    );

    if (
      leadDrawerStoreAction === "createLeadActivityDone" ||
      leadDrawerStoreAction === "updateLeadActivityDone"
    ) {
      this.setState({
        dialer: {
          id: null,
          body: "",
          callSid: "",
          callStatus: "",
        },
      });
    }
  }

  setDialerField(dialer) {
    this.setState((prevState) => ({
      dialer: {
        ...prevState.dialer,
        id: dialer.id,
        body: dialer.body,
        callSid: dialer.callSid,
        callStatus: dialer.callStatus,
      },
    }));
  }

  enableDrawerControls = () => {
    this.setState({ isDialerOpen: false });
  };

  setCurrentFormWithData = (currentFormDetails) => {
    const newState = { currentForm: currentFormDetails.form };

    if (currentFormDetails.form === "email") {
      newState.email = currentFormDetails.data.object;
      newState.schedule = currentFormDetails.data.schedule;
    }

    if (currentFormDetails.form === "text-message") {
      newState.smsMessage = currentFormDetails.data.object;
      newState.schedule = currentFormDetails.data.schedule;
    }

    this.containerEle?.scrollTo(0, 0);

    this.setState(newState);
  };

  /**
   * Calculate current recordIndex based on query.page, query.per and current row index
   * @param {Object} query - Current scope query
   * @param rowIdx - Current row index of lead in the table
   * @returns {number} recordIndex - Calculated full record index of the data set
   */
  recordIndex(query, rowIdx) {
    return (query.page - 1) * query.per + rowIdx;
  }

  loadLead(props) {
    const { lead, loadLeadID } = props;

    let leadID = loadLeadID;
    if (lead) {
      leadID = lead.id;
    }

    const params = GlobalContainer.urlParams();
    params.lead = leadID;

    const searchQuery = qs.stringify(
      Object.assign(params, { _t: Rails.helpers.currentTeam.id }),
      { arrayFormat: "brackets" },
    );

    // push browser state to opening lead URL
    browserHistory.push(
      `${window.location.pathname}?${decodeURI(searchQuery)}${
        window.location.hash
      }`,
      { lead: leadID },
    );

    // Re-load Lead with extra data from Lead property
    if (!lead && loadLeadID) {
      LeadDrawerActions.loadLead(loadLeadID);
    }
  }

  switchForm(formName, resetDialer = true) {
    const { currentForm } = this.state;
    let newForm = formName;

    const params = GlobalContainer.urlParams();
    const searchQuery = qs.stringify(params, {
      arrayFormat: "brackets",
    });

    if (isFormNameValid(newForm)) {
      // clear form select if current form button is selected again
      if (newForm === currentForm) {
        newForm = null;
        // clear current browser state to original path
        browserHistory.push(
          `${window.location.pathname}?${decodeURI(searchQuery)}`,
          {},
        );
      } else {
        // push browser state to opening lead URL
        browserHistory.push(
          `${window.location.pathname}?${decodeURI(searchQuery)}#${formName}`,
        );
      }
    } else if (
      newForm === null &&
      isFormNameValid(window.location.hash.replace("#", ""))
    ) {
      // clear hash when form is explicitly closed
      browserHistory.push(
        `${window.location.pathname}?${decodeURI(searchQuery)}`,
        {},
      );
    }

    if (newForm && this.timeline && this.timeline.scrollTo) {
      this.timeline.scrollTo(0, 0);
    }

    const newState = { currentForm: newForm };

    if (newForm === "email") {
      newState.email = null;
      newState.schedule = null;
    }

    if (newForm === "text-message") {
      newState.smsMessage = null;
      newState.schedule = null;
    }

    if (resetDialer) {
      newState.dialer = {
        id: null,
        body: "",
        callSid: "",
        callStatus: "",
      };
    }

    this.setState(newState);
  }

  renderMainSection(lead, query, currentForm) {
    const { leadDrawerState } = this.state;
    const jobsFeatureVariant = getFeatureFlag(
      process.env.SHAKAPACKER_PUBLIC_ENABLE_JOBS_ENTITY_FLAG,
    );
    const isJobsFeatureEnabled = jobsFeatureVariant === "displayed";
    const isCourtedEnabled = checkFeatureFlag(
      process.env.SHAKAPACKER_PUBLIC_COURTED_DATA_SOURCE_FLAG,
    );
    const isRetentionProduct = GlobalContainer.product() === "retention";
    const currentPath = window.location.pathname;
    const isJobsPageAndLeadInRetention =
      currentPath.includes("/recruiter/jobs") && lead.retention;

    const courtedSection = isCourtedEnabled && (
      <LeadDrawerCourtedDetails
        lead={lead}
        module={isRetentionProduct ? "retention" : "recruiting"}
      />
    );

    return (
      <div className="order-md-2 lead-drawer-details-panel col-md-4 bg-grey-lightest bl bb">
        <div className="pt15">
          <LeadDrawerMainDetails
            key={`main-details-${lead.id}`}
            lead={lead}
            query={query}
          />
        </div>

        {Rails.helpers.currentTeam?.id && (
          <AdditionalDetailsSection
            teamId={Rails.helpers.currentTeam.id}
            leadId={lead.id}
            generation={this.state.customFieldsRefreshVersion}
            isAdmin={Rails.helpers.currentUser?.role?.name === "admin"}
            onBusyChange={this.handleCustomFieldsBusy}
            onNavigationGuardChange={this.setCustomFieldsNavigationGuard}
            onSaved={this.handleCustomFieldsSaved}
          />
        )}

        <div className="bt pt15">
          <LeadDrawerTasks
            {...leadDrawerState}
            key={`tasks-details-${lead.id}`}
            lead={lead}
            query={query}
          />
        </div>

        {this.isDmsEnabled && (
          <div className="bt pt15">
            <LeadDrawerDocuments
              {...leadDrawerState}
              key={`documents-details-${lead.id}`}
              leadID={lead.id}
              query={query}
            />
          </div>
        )}

        {isRetentionProduct ? (
          <div>
            <div className="bt pt15">
              <LeadDrawerRetentionDetails
                key={`retention-details-${lead.id}`}
                lead={lead}
                query={query}
              />
            </div>

            <div className="bt pt15">
              <LeadDrawerRecruitingDetails
                key={`recruiting-details-${lead.id}`}
                lead={lead}
                query={query}
              />
            </div>

            <div className="bt pt15">
              <LeadDrawerRelitixDetails
                key={`relitix-details-${lead.id}`}
                lead={lead}
              />
            </div>

            <div className="bt pt15">
              <LeadDrawerAgentAlerts
                key={`agent-alerts-${lead.id}`}
                lead={lead}
              />
            </div>

            {courtedSection}

            {isJobsFeatureEnabled && (
              <div className="bt pt15">
                <LeadDrawerJobs key={`jobs-${lead.id}`} leadId={lead.id} />
              </div>
            )}
          </div>
        ) : (
          <div>
            {isJobsPageAndLeadInRetention && (
              <div className="bt pt15">
                <LeadDrawerRetentionDetails
                  key={`retention-details-${lead.id}`}
                  lead={lead}
                  query={query}
                />
              </div>
            )}

            <div className="bt pt15">
              <LeadDrawerRecruitingDetails
                key={`recruiting-details-${lead.id}`}
                lead={lead}
                query={query}
              />
            </div>

            <div className="bt pt15">
              <LeadDrawerRelitixDetails
                key={`relitix-details-${lead.id}`}
                lead={lead}
              />
            </div>

            <div className="bt pt15">
              <LeadDrawerAgentAlerts
                key={`agent-alerts-${lead.id}`}
                lead={lead}
              />
            </div>

            {courtedSection}

            {isJobsFeatureEnabled && (
              <div className="bt pt15">
                <LeadDrawerJobs key={`jobs-${lead.id}`} leadId={lead.id} />
              </div>
            )}

            {!isJobsPageAndLeadInRetention && (
              <div className="bt pt15">
                <LeadDrawerPipelineDetails
                  key={`recruiting-details-${lead.id}`}
                  lead={lead}
                  query={query}
                />
              </div>
            )}
          </div>
        )}

        <div className="bt pt15 pb15">
          <LeadDrawerSocialLinks key={`social-links-${lead.id}`} lead={lead} />
        </div>
      </div>
    );
  }

  renderFormSection() {
    const {
      currentForm,
      leadDrawerState,
      dialer,
      activeKey,
      email,
      smsMessage,
      schedule,
    } = this.state;
    const { activityId, defaultTab } = this.props;

    return (
      <div
        className="pl0 pr0 order-md-1 col-md-8 lead-drawer-form-panel"
        ref={(el) => (this.timeline = el)}
      >
        {currentForm && (
          <div className="pt15 pb15 bg-grey-lightest bb">
            <LeadDrawerForms
              currentForm={currentForm}
              surface="contact"
              dialer={dialer}
              {...leadDrawerState}
              email={email}
              smsMessage={smsMessage}
              schedule={schedule}
            />
          </div>
        )}

        <Tabs
          id="lead_drawer_tabs"
          activeKey={activeKey}
          onSelect={(k) => this.setState({ activeKey: k })}
          className="px-2 data-tabs clearfix mt10"
          mountOnEnter
          unmountOnExit
        >
          <Tab
            eventKey="timeline"
            title="Timeline"
            className="p10 lead-drawer-timeline-pane"
          >
            <>
              <div className="bb p15">
                <LeadDrawerLabels {...leadDrawerState} />
              </div>

              <div className="bb p15">
                <LeadDrawerNotes {...leadDrawerState} />
              </div>

              <div className="bb p15">
                <LeadDrawerCampaignSubscriptions {...leadDrawerState} />
              </div>

              <div className="p15">
                <div className="timeline">
                  <LeadDrawerTimeline
                    refreshVersion={this.state.activityRefreshVersion}
                    activityId={activityId}
                    defaultTab={defaultTab}
                    setCurrentFormWithData={this.setCurrentFormWithData}
                    {...leadDrawerState}
                  />
                </div>
              </div>
            </>
          </Tab>

          {this.isResumeTabEnabled && (
            <Tab eventKey="resume" title="Resume" className="p1">
              <ResumeDrawer />
            </Tab>
          )}
        </Tabs>
      </div>
    );
  }

  handleCustomFieldsBusy = (customFieldsBusy) =>
    this.setState({ customFieldsBusy });

  handleCustomFieldsSaved = (leadId) => {
    this.setState((state) =>
      String(state.leadDrawerState.lead?.id) === String(leadId)
        ? { activityRefreshVersion: state.activityRefreshVersion + 1 }
        : null,
    );
  };

  setCustomFieldsNavigationGuard = (guard) => {
    this.customFieldsNavigationGuard = guard;
  };

  handleCustomFieldsCleared = (teamId, leadId) => {
    if (String(Rails.helpers.currentTeam?.id) !== String(teamId)) {
      return;
    }
    this.setState((state) =>
      String(state.leadDrawerState.lead?.id) === String(leadId)
        ? {
            customFieldsRefreshVersion: state.customFieldsRefreshVersion + 1,
            activityRefreshVersion: state.activityRefreshVersion + 1,
          }
        : null,
    );
  };

  render() {
    const {
      query,
      totalCount,
      rowIdx,
      reportItemIndex,
      recordsLength,
      handleReportPrevClick,
      handleReportNextClick,
      activityId,
      defaultTab,
    } = this.props;

    const { currentForm, leadDrawerState, isDialerOpen } = this.state;
    const { lead, loadingLead, recordIndex } = leadDrawerState;
    const currentPath = window.location.pathname;
    const isJobsPageAndLeadInRetention =
      lead && currentPath.includes("/recruiter/jobs") && lead.retention;

    let showNextPrev = false;
    let prevClickMethod = null;
    let nextClickMethod = null;

    // if records query is present and has marked currentIndex or item is a row - assign prev/next handling
    if (lead && query && (recordIndex || typeof rowIdx === "number")) {
      let index = recordIndex;
      if (typeof index === "undefined") {
        index = this.recordIndex(query, rowIdx);
      }

      showNextPrev = true;

      if (index > 0) {
        prevClickMethod = this.handlePrevClick;
      }

      if (index < totalCount - 1) {
        nextClickMethod = this.handleNextClick;
      }
    }

    if (typeof reportItemIndex === "number") {
      showNextPrev = true;
      if (reportItemIndex > 0) {
        prevClickMethod = handleReportPrevClick;
      }
      if (reportItemIndex < recordsLength) {
        nextClickMethod = handleReportNextClick;
      }
    }

    if (loadingLead && !lead) {
      return (
        <Drawer size="wide">
          <div className="row pl15">
            <div className="col-xl-9 col-sm-8 p15">
              <FontAwesomeIcon
                icon="far fa-spinner"
                pulse
                size="lg"
                className="mr5"
              />{" "}
              Loading Lead...
            </div>
          </div>
        </Drawer>
      );
    }

    if (!loadingLead && !lead) {
      return (
        <Drawer size="wide">
          <div className="row pl15">
            <div className="col-xl-9 col-sm-8 p15">Lead Not Found</div>
          </div>
        </Drawer>
      );
    }

    let drawerContent = (
      <>
        {this.renderMainSection(lead, query, currentForm)}
        {this.renderFormSection()}
      </>
    );

    if (Rails.isMobile) {
      drawerContent = (
        <>
          {this.renderFormSection()}
          {this.renderMainSection(lead, query, currentForm)}
        </>
      );
    }

    return (
      <Drawer
        size="wide"
        item={lead}
        reportItemIndex={reportItemIndex}
        showNextPrev={showNextPrev}
        handlePrevClick={prevClickMethod}
        handleNextClick={nextClickMethod}
        isLoading={loadingLead}
        isDialerOpen={isDialerOpen}
        navigationDisabled={this.state.customFieldsBusy}
        beforeNavigate={() => this.customFieldsNavigationGuard?.() !== false}
        scrollable
        contentClass="lead-drawer-modal-content"
      >
        <LeadDrawerHeader
          {...leadDrawerState}
          query={query}
          currentForm={currentForm}
          switchForm={this.switchForm}
          handleOpenDialer={this.handleOpenDialer}
          onCustomFieldsCleared={this.handleCustomFieldsCleared}
          beforeReassign={() => this.customFieldsNavigationGuard?.() !== false}
          isJobsPageAndLeadInRetention={isJobsPageAndLeadInRetention}
        />

        {/*
          Inner scroller for the lead drawer body. `.lead-drawer-modal-content`
          (drawer.scss) turns `modal-content` into a flex column so this row
          gets the remaining space below `LeadDrawerHeader` and scrolls
          independently from the activity list (which has its own fixed-height
          scroll inside `.lead-drawer-activities-fill`). Two independent
          scrollers — outer for Labels/Notes/Campaigns/Pinned growth, inner
          for paginating activities.
        */}
        <div
          className="row lead-drawer-body-scroll"
          ref={(el) => (this.containerEle = el)}
        >
          {drawerContent}
        </div>
      </Drawer>
    );
  }
}

LeadDrawer.defaultProps = {
  defaultAction: null,
  lead: null,
  loadLeadID: null, // if loadLeadID presents, load it
  activityId: null,
  defaultTab: null,
  table: null,
  query: null,
  totalCount: 0,
  rowIdx: null,
  reportItemIndex: null,
  recordsLength: null,
  handleReportPrevClick: () => false,
  handleReportNextClick: () => false,
};

LeadDrawer.propTypes = {
  defaultAction: PropTypes.string,
  lead: PropTypes.shape({}),
  loadLeadID: PropTypes.number,
  activityId: PropTypes.number,
  defaultTab: PropTypes.string,
  table: PropTypes.shape({}),
  query: PropTypes.shape({}),
  totalCount: PropTypes.number,
  rowIdx: PropTypes.number,
  reportItemIndex: PropTypes.number,
  recordsLength: PropTypes.number,
  handleReportPrevClick: PropTypes.func,
  handleReportNextClick: PropTypes.func,
  skipPipelineRedirect: PropTypes.bool,
};

/**
 * LeadDrawer subscribes to LeadDrawerStore directly. The old Flux Container
 * wrapper invokes native ES classes without `new` under React 18.
 */
export default LeadDrawer;
